/**
 * URL keys that carry a cross-module navigation origin (see
 * `navigationOrigin.ts`). Kept in a dependency-free module so the search
 * builders that strip them can import it without an import cycle.
 */
export const ORIGIN_SEARCH_KEYS = [
  "originType",
  "originId",
  "originSearch",
  "originParent",
  // Retired Work Order-only keys, still read from older links.
  "originWorkOrderId",
  "originWorkOrderSearch",
] as const;

/**
 * Removes every origin key from a search object, so origin context never
 * leaks into list/return links, nested origins, or copied links. The type is
 * kept as-is: origin keys are optional on every search type that has them,
 * and `Omit` would erase the known keys of passthrough search types.
 */
export function stripOriginSearch<T extends object>(search: T): T {
  const stripped = { ...search } as Record<string, unknown>;
  for (const key of ORIGIN_SEARCH_KEYS) delete stripped[key];
  return stripped as T;
}

/** Removes origin keys from an absolute URL, e.g. before copying it. */
export function stripOriginFromUrl(href: string): string {
  const url = new URL(href);
  for (const key of ORIGIN_SEARCH_KEYS) url.searchParams.delete(key);
  return url.toString();
}
